import { useState, useEffect } from 'react'
import type { ChangeEvent } from 'react'
import { useAtom, useAtomValue } from 'jotai'
import { Modal, Typography, notification } from 'antd'
import { Eye, EyeOff, X } from 'lucide-react'

import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { authApi, changePasswordSchema } from '@/api/auth.api'
import { sessionAtom } from '@/store/auth.atom'
import { isPasswordModalOpenAtom } from '@/store/password-modal.atom'
import { useLogout } from '@/hooks/use-logout'

const { Title, Text } = Typography
type ModalView = 'form' | 'otp'

export function ChangePasswordModal() {
  const [isOpen, setIsOpen] = useAtom(isPasswordModalOpenAtom)
  const session = useAtomValue(sessionAtom)
  const { mutate: logout } = useLogout()

  // --- States ---
  const [view, setView] = useState<ModalView>('form')
  const [isLoading, setIsLoading] = useState(false)
  const [errors, setErrors] = useState<Partial<Record<string, string>>>({})

  // Form password states
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showCurrentPassword, setShowCurrentPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  // Form OTP states
  // const [otp, setOtp] = useState(['', '', '', '', '', ''])
  // const otpRefs = useRef<(HTMLInputElement | null)[]>([])
  const [countdown, setCountdown] = useState(60)

  // --- Effects ---
  useEffect(() => {
    let timer: NodeJS.Timeout
    if (view === 'otp' && countdown > 0) {
      timer = setInterval(() => {
        setCountdown((prev) => prev - 1)
      }, 1000)
    }
    return () => clearInterval(timer)
  }, [view, countdown])

  // --- Handlers ---
  const resetState = () => {
    setCurrentPassword('')
    setNewPassword('')
    setConfirmPassword('')
    // setOtp(['', '', '', '', '', ''])
    setErrors({})
    setView('form')
    setCountdown(60)
  }

  const handleClose = () => {
    setIsOpen(false)
    setTimeout(() => resetState(), 300)
  }

  const handleFieldChange =
    (
      field: 'currentPassword' | 'newPassword' | 'confirmPassword',
      setter: React.Dispatch<React.SetStateAction<string>>,
    ) =>
    (e: ChangeEvent<HTMLInputElement> | any) => {
      const val = e?.target?.value ?? e
      setter(val)

      if (errors[field]) {
        setErrors((prev) => ({ ...prev, [field]: undefined }))
      }

      if (field === 'newPassword' || field === 'confirmPassword') {
        validateFieldRealtime(field, val)
      }
    }

  // Real-time Validation for Password
  const validateFieldRealtime = (
    field: 'newPassword' | 'confirmPassword',
    val: string,
  ) => {
    const formData = {
      email: session?.user.email || 'valid@email.com',
      currentPassword: currentPassword || 'dummy123',
      newPassword: field === 'newPassword' ? val : newPassword,
      confirmPassword: field === 'confirmPassword' ? val : confirmPassword,
    }

    const result = changePasswordSchema.safeParse(formData)

    if (!result.success) {
      const fieldIssue = result.error.issues.find((i) => i.path[0] === field)
      const confirmIssue = result.error.issues.find(
        (i) => i.path[0] === 'confirmPassword',
      )

      setErrors((prev) => ({
        ...prev,
        [field]: fieldIssue?.message,
        ...(field === 'newPassword' && formData.confirmPassword
          ? { confirmPassword: confirmIssue?.message }
          : {}),
      }))
    } else {
      setErrors((prev) => ({
        ...prev,
        [field]: undefined,
        ...(field === 'newPassword' ? { confirmPassword: undefined } : {}),
      }))
    }
  }

  const handlePasswordSubmit = async (e: React.SyntheticEvent) => {
    e.preventDefault()
    setErrors({})

    if (!session?.user.email) {
      notification.error({
        title:
          'Không tìm thấy email của tài khoản. Vui lòng thử đăng nhập lại.',
      })
      return
    }

    const validationResult = changePasswordSchema.safeParse({
      email: session.user.email,
      currentPassword,
      newPassword,
      confirmPassword,
    })

    if (!validationResult.success) {
      const fieldErrors: Partial<Record<string, string>> = {}
      for (const issue of validationResult.error.issues) {
        const field = String(issue.path[0])
        if (!fieldErrors[field]) fieldErrors[field] = issue.message
      }
      setErrors(fieldErrors)
      return
    }

    setIsLoading(true)
    try {
      const res = await authApi.changePassword(validationResult.data)
      notification.success({
        title: res?.responseMessage || 'Đổi mật khẩu thành công!',
      })

      handleClose()
      logout()
    } catch (err: any) {
      setErrors({
        currentPassword: err.message || 'Đã xảy ra lỗi khi đổi mật khẩu',
      })
      setIsLoading(false)
    }
  }

  // const handleOtpSubmit = async (e: React.SyntheticEvent) => {
  //   e.preventDefault()
  //   setErrors({})

  //   const joinedOtp = otp.join('')
  //   if (joinedOtp.length < 6) {
  //     setErrors({ otp: 'Vui lòng nhập đủ 6 số mã OTP' })
  //     return
  //   }

  //   setIsLoading(true)
  //   try {
  //     const payload = {
  //       email: session!.user.email,
  //       currentPassword,
  //       newPassword,
  //       confirmPassword,
  //       otp: joinedOtp,
  //     }

  //     const res = await authApi.changePassword(payload)
  //     notification.success({
  //       title: res?.responseMessage || 'Đổi mật khẩu thành công!',
  //     })
  //     handleClose()
  //   } catch (err: any) {
  //     console.error(err)
  //   } finally {
  //     setIsLoading(false)
  //   }
  // }

  // const handleOtpChange = (index: number, value: string) => {
  //   if (value.length > 1) value = value.slice(-1)
  //   const newOtp = [...otp]
  //   newOtp[index] = value
  //   setOtp(newOtp)

  //   if (value && index < 5) {
  //     otpRefs.current[index + 1]?.focus()
  //   }
  //   if (errors.otp) setErrors({ ...errors, otp: undefined })
  // }

  // const handleOtpKeyDown = (
  //   index: number,
  //   e: KeyboardEvent<HTMLInputElement>,
  // ) => {
  //   if (e.key === 'Backspace' && !otp[index] && index > 0) {
  //     otpRefs.current[index - 1]?.focus()
  //   }
  // }

  // const handleResendOtp = () => {
  //   setCountdown(60)
  //   notification.success({ title: 'Đã gửi lại mã OTP' })
  // }

  return (
    <Modal
      open={isOpen}
      onCancel={handleClose}
      footer={null}
      closable={false}
      centered
      width={480}
      className="[&_.ant-modal-container]:bg-white [&_.ant-modal-content]:!p-5 sm:[&_.ant-modal-content]:!p-8 [&_.ant-modal-content]:!rounded-2xl shadow-xl max-w-[95vw] sm:max-w-[480px]"
    >
      <div className="flex flex-col relative">
        <Button
          variant="ghost"
          size="icon"
          onClick={handleClose}
          className="absolute -top-2 -right-2 sm:-top-3 sm:-right-3 hover:bg-gray-100 h-8 w-8 z-10 rounded-full text-gray-500"
        >
          <X size={18} className="sm:w-5 sm:h-5" />
        </Button>

        {/* --- DYNAMIC HEADER --- */}
        <div className="mb-4 sm:mb-6 text-center flex flex-col items-center">
          <img
            src="/logo_tthcc 1.png"
            alt="Logo"
            className="h-12 w-12 sm:h-16 sm:w-16 object-contain drop-shadow-sm mb-2"
          />
          <Title
            level={2}
            className="!m-0 text-[#1A202C] !text-xl sm:!text-2xl"
          >
            {view === 'form' ? 'Đổi mật khẩu' : 'Xác nhận OTP'}
          </Title>
        </div>

        {/* --- VIEW 1: PASSWORD FORM --- */}
        {view === 'form' && (
          <form onSubmit={handlePasswordSubmit}>
            <div className="flex flex-col gap-3.5 sm:gap-4">
              {/* Current Password */}
              <div>
                <label
                  htmlFor="current-password-input"
                  className="mb-1.5 block text-sm sm:text-base font-medium"
                >
                  Mật khẩu hiện tại <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Input
                    id="current-password-input"
                    type={showCurrentPassword ? 'text' : 'password'}
                    size="large"
                    value={currentPassword}
                    onChange={handleFieldChange(
                      'currentPassword',
                      setCurrentPassword,
                    )}
                    placeholder="••••••••"
                    autoComplete="off"
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    className="absolute right-2 top-1/2 -translate-y-1/2 border-0 !bg-transparent hover:!border-0 focus:!border-0 focus:outline-none"
                    onClick={() => setShowCurrentPassword((prev) => !prev)}
                    tabIndex={-1}
                  >
                    {showCurrentPassword ? (
                      <EyeOff size={18} className="sm:w-5 sm:h-5" />
                    ) : (
                      <Eye size={18} className="sm:w-5 sm:h-5" />
                    )}
                  </Button>
                </div>
                {errors.currentPassword && (
                  <Text type="danger" className="mt-1 block text-xs sm:text-sm">
                    {errors.currentPassword}
                  </Text>
                )}
              </div>

              {/* New Password */}
              <div>
                <label
                  htmlFor="new-password-input"
                  className="mb-1.5 block text-sm sm:text-base font-medium"
                >
                  Mật khẩu mới <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Input
                    id="new-password-input"
                    type={showNewPassword ? 'text' : 'password'}
                    size="large"
                    value={newPassword}
                    onChange={handleFieldChange('newPassword', setNewPassword)}
                    placeholder="••••••••"
                    autoComplete="new-password"
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    className="absolute right-2 top-1/2 -translate-y-1/2 border-0 !bg-transparent hover:!border-0 focus:!border-0 focus:outline-none"
                    onClick={() => setShowNewPassword((prev) => !prev)}
                    tabIndex={-1}
                  >
                    {showNewPassword ? (
                      <EyeOff size={18} className="sm:w-5 sm:h-5" />
                    ) : (
                      <Eye size={18} className="sm:w-5 sm:h-5" />
                    )}
                  </Button>
                </div>
                {errors.newPassword && (
                  <Text type="danger" className="mt-1 block text-xs sm:text-sm">
                    {errors.newPassword}
                  </Text>
                )}
              </div>

              {/* Confirm Password */}
              <div>
                <label
                  htmlFor="confirm-password-input"
                  className="mb-1.5 block text-sm sm:text-base font-medium"
                >
                  Xác nhận lại mật khẩu <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Input
                    id="confirm-password-input"
                    type={showConfirmPassword ? 'text' : 'password'}
                    size="large"
                    value={confirmPassword}
                    onChange={handleFieldChange(
                      'confirmPassword',
                      setConfirmPassword,
                    )}
                    placeholder="••••••••"
                    autoComplete="new-password"
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    className="absolute right-2 top-1/2 -translate-y-1/2 border-0 !bg-transparent hover:!border-0 focus:!border-0 focus:outline-none"
                    onClick={() => setShowConfirmPassword((prev) => !prev)}
                    tabIndex={-1}
                  >
                    {showConfirmPassword ? (
                      <EyeOff size={18} className="sm:w-5 sm:h-5" />
                    ) : (
                      <Eye size={18} className="sm:w-5 sm:h-5" />
                    )}
                  </Button>
                </div>
                {errors.confirmPassword && (
                  <Text type="danger" className="mt-1 block text-xs sm:text-sm">
                    {errors.confirmPassword}
                  </Text>
                )}
              </div>

              <div className="mt-1 sm:mt-2 flex gap-2.5 sm:gap-3">
                <Button
                  onClick={handleClose}
                  variant="outline"
                  size="lg"
                  className="flex-1 text-[14px]"
                  disabled={isLoading}
                >
                  Đóng
                </Button>
                <Button
                  htmlType="submit"
                  variant="primary"
                  size="lg"
                  className="flex-1 text-[14px]"
                  loading={isLoading}
                >
                  Xác nhận
                </Button>
              </div>
            </div>
          </form>
        )}

        {/* {view === 'otp' && (
          <form onSubmit={handleOtpSubmit}>
            <div className="flex flex-col gap-6">
              <div className="flex justify-center gap-2 sm:gap-3">
                {otp.map((digit, idx) => (
                  <Input
                    key={idx}
                    ref={(e: HTMLInputElement | null) => {
                      otpRefs.current[idx] = e
                    }}
                    type="text"
                    size="large"
                    inputMode="numeric"
                    value={digit}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => handleOtpChange(idx, e.target.value)}
                    onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => handleOtpKeyDown(idx, e)}
                    maxLength={1}
                    autoComplete="one-time-code"
                    className="h-12 w-12 text-center text-xl sm:h-14 sm:w-14 focus-visible:border-[#CE7A58] focus-visible:ring-1 focus-visible:ring-[#CE7A58]"
                  />
                ))}
              </div>

              {errors.otp && (
                <Text type="danger" className="-mt-4 block text-center text-sm">
                  {errors.otp}
                </Text>
              )}

              <div className="text-right -mt-2">
                <button
                  type="button"
                  onClick={handleResendOtp}
                  disabled={countdown > 0}
                  className="cursor-pointer border-none bg-transparent text-[#0088FF] text-sm hover:underline disabled:text-gray-400 disabled:cursor-not-allowed disabled:no-underline"
                >
                  Gửi lại {countdown > 0 ? `(${countdown}s)` : ''}
                </button>
              </div>

              <div className="flex gap-3">
                <Button                  
                  variant="outline"
                  size="lg"
                  className="flex-1 text-[14px] !rounded-full"
                  onClick={() => setView('form')}
                  disabled={isLoading}
                >
                  Quay lại
                </Button>
                <Button
                  htmlType="submit"
                  variant="primary"
                  size="lg"
                  className="flex-1 text-[14px] !rounded-full bg-[#CC7A5C] hover:bg-[#B36A50]"
                  loading={isLoading}
                  disabled={otp.join('').length < 6}
                >
                  Xác nhận
                </Button>
              </div>
            </div>
          </form>
        )} */}
      </div>
    </Modal>
  )
}
